export interface SpecialDateRange {
  start: string;
  end: string;
}

export type SpecialDateInput = string | SpecialDateRange;

type SpecialDateFormat = 'api' | 'local';

interface DateParts {
  year: number;
  month: number;
  day: number;
}

const API_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const LOCAL_DATE_PATTERN = /^(\d{2})\.(\d{2})\.(\d{4})$/;
const DAY_MS = 24 * 60 * 60 * 1000;

const toDateParts = (value: string): DateParts => {
  const apiMatch = value.match(API_DATE_PATTERN);

  if (apiMatch) {
    return validateDateParts({
      year: Number(apiMatch[1]),
      month: Number(apiMatch[2]),
      day: Number(apiMatch[3]),
    });
  }

  const localMatch = value.match(LOCAL_DATE_PATTERN);

  if (localMatch) {
    return validateDateParts({
      year: Number(localMatch[3]),
      month: Number(localMatch[2]),
      day: Number(localMatch[1]),
    });
  }

  throw new Error(`Unsupported special date format: ${value}`);
};

const validateDateParts = (parts: DateParts): DateParts => {
  const utcTimestamp = Date.UTC(parts.year, parts.month - 1, parts.day);
  const utcDate = new Date(utcTimestamp);

  if (
    utcDate.getUTCFullYear() !== parts.year ||
    utcDate.getUTCMonth() !== parts.month - 1 ||
    utcDate.getUTCDate() !== parts.day
  ) {
    throw new Error(
      `Invalid special date value: ${parts.year}-${parts.month}-${parts.day}`,
    );
  }

  return parts;
};

const pad = (value: number): string => value.toString().padStart(2, '0');

const formatDateParts = (
  parts: DateParts,
  format: SpecialDateFormat,
): string =>
  format === 'api'
    ? `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`
    : `${pad(parts.day)}.${pad(parts.month)}.${parts.year}`;

const compareApiDates = (left: string, right: string): number =>
  left.localeCompare(right);

const addDays = (apiDate: string, days: number): string => {
  const parts = toDateParts(apiDate);
  const utcTimestamp = Date.UTC(parts.year, parts.month - 1, parts.day);

  return formatDateParts(
    validateDatePartsFromTimestamp(utcTimestamp + days * DAY_MS),
    'api',
  );
};

const validateDatePartsFromTimestamp = (utcTimestamp: number): DateParts => {
  const utcDate = new Date(utcTimestamp);

  return {
    year: utcDate.getUTCFullYear(),
    month: utcDate.getUTCMonth() + 1,
    day: utcDate.getUTCDate(),
  };
};

const toNormalizedApiDate = (value: string): string =>
  formatDateParts(toDateParts(value), 'api');

const toOutputDate = (apiDate: string, format: SpecialDateFormat): string =>
  format === 'api' ? apiDate : formatDateParts(toDateParts(apiDate), 'local');

export const normalizeSpecialDate = (
  value: string,
  format: SpecialDateFormat = 'api',
): string => formatDateParts(toDateParts(value), format);

export const expandSpecialDateRanges = (
  values: SpecialDateInput[] | null | undefined,
  format: SpecialDateFormat = 'api',
): string[] => {
  if (!values?.length) {
    return [];
  }

  const expandedDates = new Set<string>();

  values.forEach((value) => {
    if (typeof value === 'string') {
      expandedDates.add(toNormalizedApiDate(value));
      return;
    }

    const normalizedStart = toNormalizedApiDate(value.start);
    const normalizedEnd = toNormalizedApiDate(value.end);
    const rangeStart =
      compareApiDates(normalizedStart, normalizedEnd) <= 0
        ? normalizedStart
        : normalizedEnd;
    const rangeEnd =
      rangeStart === normalizedStart ? normalizedEnd : normalizedStart;

    for (
      let currentDate = rangeStart;
      compareApiDates(currentDate, rangeEnd) <= 0;
      currentDate = addDays(currentDate, 1)
    ) {
      expandedDates.add(currentDate);
    }
  });

  return Array.from(expandedDates)
    .sort(compareApiDates)
    .map((apiDate) => toOutputDate(apiDate, format));
};

export const compactSpecialDates = (
  values: string[] | null | undefined,
  format: SpecialDateFormat = 'api',
): SpecialDateRange[] => {
  const sortedDates = expandSpecialDateRanges(values, 'api');

  if (!sortedDates.length) {
    return [];
  }

  const groups: SpecialDateRange[] = [];
  let rangeStart = sortedDates[0];
  let rangeEnd = sortedDates[0];

  for (let index = 1; index < sortedDates.length; index += 1) {
    const currentDate = sortedDates[index];

    if (currentDate === addDays(rangeEnd, 1)) {
      rangeEnd = currentDate;
      continue;
    }

    groups.push({
      start: toOutputDate(rangeStart, format),
      end: toOutputDate(rangeEnd, format),
    });
    rangeStart = currentDate;
    rangeEnd = currentDate;
  }

  groups.push({
    start: toOutputDate(rangeStart, format),
    end: toOutputDate(rangeEnd, format),
  });

  return groups;
};

export const formatSpecialDateDisplayValues = (
  values: string[] | null | undefined,
): string[] =>
  compactSpecialDates(values, 'local').map(({ start, end }) =>
    start === end ? start : `${start}–${end}`,
  );
